import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', teal: '#00C9B1',
  coral: '#FF6B6B', text: '#E8EDF5', sub: '#C5CDD8',
  lav: '#A78BFA', sky: '#38BDF8',
};

const ACTION_META = {
  'auth.login':          { label: 'Login',               color: C.teal,  icon: '🔑' },
  'sale.created':        { label: 'Sale Created',         color: C.gold,  icon: '🛒' },
  'sale.voided':         { label: 'Sale Voided',          color: C.coral, icon: '🗑' },
  'sale.returned':       { label: 'Sale Returned',        color: C.coral, icon: '↩' },
  'staff.added':         { label: 'Staff Added',          color: C.teal,  icon: '👤' },
  'staff.updated':       { label: 'Staff Updated',        color: C.sky,   icon: '✏' },
  'staff.removed':       { label: 'Staff Removed',        color: C.coral, icon: '👤' },
  'payslip.approved':    { label: 'Payslip Approved',     color: C.gold,  icon: '✅' },
  'payslip.paid':        { label: 'Payslip Paid',         color: C.teal,  icon: '💰' },
  'payslip.disbursed':   { label: 'Payslip Disbursed',    color: C.teal,  icon: '🏦' },
  'advance.issued':      { label: 'Advance Issued',       color: C.lav,   icon: '💸' },
  'month.closed':        { label: 'Month Closed',         color: C.gold,  icon: '📅' },
  'month.reopened':      { label: 'Month Reopened',       color: C.coral, icon: '📅' },
  'member.invited':      { label: 'Member Invited',       color: C.sky,   icon: '📧' },
  'member.removed':      { label: 'Member Removed',       color: C.coral, icon: '🚫' },
  'member.role_changed': { label: 'Role Changed',         color: C.lav,   icon: '🔄' },
  'purchase.received':   { label: 'Purchase Received',    color: C.teal,  icon: '📦' },
};

const ENTITY_FILTERS = [
  { value: '', label: 'All activity' },
  { value: 'Sale',              label: 'Sales' },
  { value: 'Staff',             label: 'Staff' },
  { value: 'Payslip',          label: 'Payroll' },
  { value: 'SalaryAdvance',    label: 'Advances' },
  { value: 'MonthClose',       label: 'Month Close' },
  { value: 'WorkspaceMember',  label: 'Team' },
  { value: 'User',             label: 'Logins' },
];

const STYLES = `
  .sa-wrap { padding: 28px 32px; max-width: 900px; }
  .sa-title { font-size: 1.35rem; font-weight: 800; color: ${C.text}; margin: 0 0 4px; }
  .sa-subtitle { color: ${C.muted}; font-size: 0.875rem; margin-bottom: 20px; }

  .sa-tabs { display: flex; gap: 4px; border-bottom: 1px solid ${C.border}; margin-bottom: 24px; }
  .sa-tab { padding: 10px 18px; font-size: 0.82rem; font-weight: 700; cursor: pointer; color: ${C.muted}; border-bottom: 3px solid transparent; margin-bottom: -1px; transition: all 0.15s; }
  .sa-tab:hover { color: ${C.text}; }
  .sa-tab.on { color: ${C.gold}; border-bottom-color: ${C.gold}; }

  .sa-loading { padding: 60px; text-align: center; color: ${C.muted}; }
  .sa-error { padding: 14px 16px; border-radius: 10px; background: rgba(255,107,107,0.08); border: 1px solid rgba(255,107,107,0.2); color: ${C.coral}; font-size: 0.875rem; }
  .sa-access-denied { padding: 40px; text-align: center; color: ${C.muted}; }
  .sa-access-denied-title { font-weight: 700; color: ${C.text}; margin-bottom: 6px; }

  .sa-score-card { background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 14px; padding: 24px 28px; display: flex; align-items: center; gap: 32px; margin-bottom: 24px; flex-wrap: wrap; }
  .sa-score-ring-wrap { display: flex; flex-direction: column; align-items: center; gap: 8px; }
  .sa-score-ring { width: 120px; height: 120px; border-radius: 50%; border-width: 8px; border-style: solid; display: flex; flex-direction: column; align-items: center; justify-content: center; background: rgba(255,255,255,0.03); }
  .sa-score-num { font-size: 2rem; font-weight: 800; font-family: 'JetBrains Mono', monospace; line-height: 1; }
  .sa-score-denom { font-size: 0.65rem; color: rgba(255,255,255,0.4); margin-top: 2px; }
  .sa-score-label { font-size: 0.82rem; font-weight: 700; }
  .sa-score-title { font-size: 1.05rem; font-weight: 700; color: ${C.text}; margin-bottom: 12px; }
  .sa-stats-row { display: flex; gap: 20px; flex-wrap: wrap; }
  .sa-stat { text-align: center; }
  .sa-stat-num { font-size: 1.4rem; font-weight: 800; font-family: 'JetBrains Mono', monospace; }
  .sa-stat-label { font-size: 0.72rem; color: ${C.muted}; margin-top: 2px; }

  .sa-category { background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 14px; padding: 20px 24px; margin-bottom: 16px; }
  .sa-category-title { font-size: 0.7rem; font-weight: 700; letter-spacing: .06em; color: ${C.gold}; text-transform: uppercase; margin-bottom: 14px; display: flex; align-items: center; gap: 8px; }
  .sa-check-item { display: flex; gap: 12px; align-items: flex-start; padding: 10px 14px; border-radius: 8px; border: 1px solid; margin-bottom: 6px; }
  .sa-check-item:last-child { margin-bottom: 0; }
  .sa-check-icon { flex-shrink: 0; margin-top: 1px; font-size: 1rem; }
  .sa-check-name { font-size: 0.85rem; font-weight: 600; color: ${C.text}; margin-bottom: 2px; }
  .sa-check-detail { font-size: 0.78rem; color: ${C.muted}; line-height: 1.5; }

  .sa-filters { display: flex; gap: 10px; margin-bottom: 16px; flex-wrap: wrap; align-items: center; }
  .sa-filter-select { padding: 8px 12px; border-radius: 8px; background: ${C.surface}; border: 1px solid ${C.border}; color: ${C.text}; font-size: 0.8rem; font-family: inherit; cursor: pointer; outline: none; }
  .sa-filter-select option { background: ${C.surface}; }
  .sa-search { flex: 1; min-width: 180px; padding: 8px 12px; border-radius: 8px; background: ${C.surface}; border: 1px solid ${C.border}; color: ${C.text}; font-size: 0.8rem; font-family: inherit; outline: none; }
  .sa-search:focus { border-color: ${C.gold}; }
  .sa-search::placeholder { color: ${C.muted}; }

  .sa-log-table { background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 14px; overflow: hidden; }
  .sa-log-header { display: grid; grid-template-columns: 160px 140px 1fr 120px; gap: 12px; padding: 10px 16px; border-bottom: 1px solid ${C.border}; }
  .sa-log-header-cell { font-size: 0.68rem; font-weight: 700; color: ${C.muted}; text-transform: uppercase; letter-spacing: 0.06em; }
  .sa-log-row { display: grid; grid-template-columns: 160px 140px 1fr 120px; gap: 12px; padding: 12px 16px; border-bottom: 1px solid rgba(255,255,255,0.03); transition: background 0.1s; align-items: center; }
  .sa-log-row:last-child { border-bottom: none; }
  .sa-log-row:hover { background: rgba(255,255,255,0.03); }

  .sa-action-chip { display: inline-flex; align-items: center; gap: 5px; font-size: 0.73rem; font-weight: 700; padding: 3px 8px; border-radius: 100px; }
  .sa-meta { font-size: 0.75rem; color: ${C.muted}; }
  .sa-meta strong { color: ${C.sub}; font-weight: 600; }
  .sa-ts { font-size: 0.72rem; color: ${C.muted}; font-family: 'JetBrains Mono', monospace; }
  .sa-actor { font-size: 0.78rem; color: ${C.sub}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .sa-actor-name { font-weight: 700; color: ${C.text}; }

  .sa-pagination { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; border-top: 1px solid ${C.border}; }
  .sa-page-btn { padding: 6px 14px; border-radius: 8px; border: 1px solid ${C.border}; background: rgba(255,255,255,0.04); color: ${C.sub}; font-size: 0.78rem; font-weight: 700; cursor: pointer; font-family: inherit; transition: all 0.15s; }
  .sa-page-btn:hover:not(:disabled) { background: rgba(255,255,255,0.08); color: ${C.text}; }
  .sa-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .sa-page-info { font-size: 0.78rem; color: ${C.muted}; }

  .sa-empty { padding: 48px; text-align: center; color: ${C.muted}; }
  .sa-empty-icon { font-size: 2rem; margin-bottom: 12px; }
  .sa-footer { font-size: 0.75rem; color: ${C.muted}; text-align: center; margin-top: 8px; }
`;

const CATEGORY_ORDER = ['Auth', 'API', 'Data', 'Infrastructure'];
const categoryIcons = {
  Auth: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  API:  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>,
  Data: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>,
  Infrastructure: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>,
};

function formatMeta(action, meta) {
  if (!meta) return null;
  if (action === 'sale.created')      return <><strong>GHS {Number(meta.total || 0).toFixed(2)}</strong>{meta.customer ? ` · ${meta.customer}` : ''}{meta.items ? ` · ${meta.items} item(s)` : ''}</>;
  if (action === 'sale.returned')     return <><strong>GHS {Number(meta.refundAmount || 0).toFixed(2)} refunded</strong>{meta.reason ? ` · ${meta.reason}` : ''}</>;
  if (action === 'staff.added')       return <><strong>{meta.name}</strong>{meta.role ? ` · ${meta.role}` : ''}</>;
  if (action === 'staff.updated')     return <strong>{meta.name}</strong>;
  if (action === 'staff.removed')     return <><strong>{meta.name}</strong> deactivated</>;
  if (action === 'payslip.approved')  return <>Payslip for {meta.month}/{meta.year} · <strong>GHS {Number(meta.netPay || 0).toFixed(2)}</strong></>;
  if (action === 'payslip.paid')      return <>Payslip for {meta.month}/{meta.year} · <strong>GHS {Number(meta.netPay || 0).toFixed(2)}</strong></>;
  if (action === 'payslip.disbursed') return <>{meta.method?.toUpperCase()} · <strong>GHS {Number(meta.netPay || 0).toFixed(2)}</strong></>;
  if (action === 'advance.issued')    return <><strong>{meta.staffName}</strong> · GHS {Number(meta.amount || 0).toFixed(2)}</>;
  if (action === 'month.closed')      return <strong>{meta.month}/{meta.year}</strong>;
  if (action === 'month.reopened')    return <strong>{meta.month}/{meta.year}</strong>;
  if (action === 'member.invited')    return <><strong>{meta.email}</strong> as {meta.role}</>;
  if (action === 'member.removed')    return <strong>{meta.name || meta.email}</strong>;
  if (action === 'member.role_changed') return <><strong>{meta.name || meta.email}</strong> → {meta.role}</>;
  return null;
}

function formatTs(ts) {
  const d = new Date(ts);
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${date} ${time}`;
}

function ActivityLog() {
  const [logs, setLogs]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [pages, setPages]     = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [entity, setEntity]   = useState('');
  const [search, setSearch]   = useState('');
  const [searchInput, setSearchInput] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page });
      if (entity) params.set('entity', entity);
      if (search) params.set('search', search);
      const r = await api.get(`/security/audit-log?${params}`);
      setLogs(r.data.logs);
      setTotal(r.data.total);
      setPages(r.data.pages);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [page, entity, search]);

  useEffect(() => { load(); }, [load]);

  const applySearch = () => { setPage(1); setSearch(searchInput); };

  return (
    <div>
      <div className="sa-filters">
        <select className="sa-filter-select" value={entity} onChange={e => { setEntity(e.target.value); setPage(1); }}>
          {ENTITY_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <input
          className="sa-search"
          placeholder="Search by action, user name or email…"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && applySearch()}
        />
        <button className="sa-page-btn" onClick={applySearch}>Search</button>
        {(search || entity) && (
          <button className="sa-page-btn" onClick={() => { setSearch(''); setSearchInput(''); setEntity(''); setPage(1); }}>Clear</button>
        )}
      </div>

      {error && <div className="sa-error">{error}</div>}

      {loading ? (
        <div className="sa-loading">Loading activity…</div>
      ) : logs.length === 0 ? (
        <div className="sa-log-table">
          <div className="sa-empty">
            <div className="sa-empty-icon">📋</div>
            <div style={{ fontWeight: 700, color: C.sub, marginBottom: 4 }}>No activity found</div>
            <div style={{ fontSize: '0.8rem' }}>Actions will appear here as your team uses the system.</div>
          </div>
        </div>
      ) : (
        <div className="sa-log-table">
          <div className="sa-log-header">
            <div className="sa-log-header-cell">Timestamp</div>
            <div className="sa-log-header-cell">Action</div>
            <div className="sa-log-header-cell">Details</div>
            <div className="sa-log-header-cell">By</div>
          </div>
          {logs.map(log => {
            const am = ACTION_META[log.action] || { label: log.action, color: C.muted, icon: '•' };
            return (
              <div key={log.id} className="sa-log-row">
                <div className="sa-ts">{formatTs(log.createdAt)}</div>
                <div>
                  <span className="sa-action-chip" style={{ background: `${am.color}18`, color: am.color }}>
                    {am.icon} {am.label}
                  </span>
                </div>
                <div className="sa-meta">{formatMeta(log.action, log.meta)}</div>
                <div className="sa-actor" title={log.userEmail}>
                  {log.userName ? <span className="sa-actor-name">{log.userName}</span> : <span style={{ color: C.muted }}>System</span>}
                </div>
              </div>
            );
          })}
          <div className="sa-pagination">
            <button className="sa-page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Previous</button>
            <span className="sa-page-info">{total} event{total !== 1 ? 's' : ''} · Page {page} of {pages}</span>
            <button className="sa-page-btn" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}

function HealthCheck() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    api.get('/security/audit')
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.error || 'Failed to load security audit'))
      .finally(() => setLoading(false));
  }, []);

  const scoreColor = s => s > 80 ? C.teal : s >= 60 ? C.gold : C.coral;
  const scoreLabel = s => s > 80 ? 'Strong' : s >= 60 ? 'Moderate' : 'Weak';
  const checkStyle = status => {
    if (status === 'pass') return { bg: 'rgba(0,201,177,0.06)',   border: 'rgba(0,201,177,0.15)',   icon: <span style={{ color: C.teal }}>✓</span> };
    if (status === 'warn') return { bg: 'rgba(240,180,41,0.08)',  border: 'rgba(240,180,41,0.2)',   icon: <span style={{ color: C.gold }}>⚠</span> };
    return                        { bg: 'rgba(255,107,107,0.08)', border: 'rgba(255,107,107,0.2)', icon: <span style={{ color: C.coral }}>✗</span> };
  };

  if (loading) return <div className="sa-loading">Running security checks…</div>;
  if (error)   return <div className="sa-error">{error}</div>;
  if (!data)   return null;

  return (
    <>
      <div className="sa-score-card">
        <div className="sa-score-ring-wrap">
          <div className="sa-score-ring" style={{ borderColor: scoreColor(data.score), boxShadow: `0 0 24px ${scoreColor(data.score)}33` }}>
            <div className="sa-score-num" style={{ color: scoreColor(data.score) }}>{data.score}</div>
            <div className="sa-score-denom">/ 100</div>
          </div>
          <div className="sa-score-label" style={{ color: scoreColor(data.score) }}>{scoreLabel(data.score)}</div>
        </div>
        <div style={{ flex: 1 }}>
          <div className="sa-score-title">Overall Security Score</div>
          <div className="sa-stats-row">
            {[['Passed', data.passed, C.teal], ['Warnings', data.warned, C.gold], ['Failed', data.failed, C.coral], ['Total', data.total, C.sub]].map(([lbl, val, color]) => (
              <div key={lbl} className="sa-stat">
                <div className="sa-stat-num" style={{ color }}>{val}</div>
                <div className="sa-stat-label">{lbl}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {CATEGORY_ORDER.map(category => {
        const items = data.checks.filter(c => c.category === category);
        if (!items.length) return null;
        return (
          <div key={category} className="sa-category">
            <div className="sa-category-title">{categoryIcons[category]}{category}</div>
            {items.map((check, i) => {
              const { bg, border, icon } = checkStyle(check.status);
              return (
                <div key={i} className="sa-check-item" style={{ background: bg, borderColor: border }}>
                  <div className="sa-check-icon">{icon}</div>
                  <div style={{ flex: 1 }}>
                    <div className="sa-check-name">{check.item}</div>
                    <div className="sa-check-detail">{check.detail}</div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
      <div className="sa-footer">Last checked: {new Date().toLocaleString()}</div>
    </>
  );
}

export default function SecurityAudit() {
  const { user } = useAuth();
  const [tab, setTab] = useState('activity');

  if (user?.role && user.role !== 'owner' && user.role !== 'admin') {
    return (
      <Layout>
        <style>{STYLES}</style>
        <div className="sa-wrap">
          <div className="sa-access-denied">
            <div style={{ fontSize: '2rem', marginBottom: 12 }}>🔒</div>
            <div className="sa-access-denied-title">Owner access required</div>
            <div style={{ fontSize: '0.85rem' }}>Security audit is only available to workspace owners and admins.</div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <style>{STYLES}</style>
      <div className="sa-wrap">
        <h1 className="sa-title">Security</h1>
        <p className="sa-subtitle">Monitor activity and verify your security configuration.</p>

        <div className="sa-tabs">
          <div className={`sa-tab${tab === 'activity' ? ' on' : ''}`} onClick={() => setTab('activity')}>Activity Log</div>
          <div className={`sa-tab${tab === 'health' ? ' on' : ''}`} onClick={() => setTab('health')}>Health Check</div>
        </div>

        {tab === 'activity' && <ActivityLog />}
        {tab === 'health'   && <HealthCheck />}
      </div>
    </Layout>
  );
}
